import { ITEM_FIELDS } from '@/graphql/queries/items';
import { ORDER_FIELDS, ORDER_ROUTE_FIELDS } from '@/graphql/queries/orders';
import { ROUTE_FIELDS, ROUTE_ORDER_FIELDS, ROUTE_STAGING_VERIFICATION_FIELDS } from '@/graphql/queries/routes';
import { STOP_FIELDS } from '@/graphql/queries/stops';
import { CLIENT_FIELDS } from '@/graphql/queries/clients';
import { MESSAGE_FIELDS } from '@/graphql/queries/messages';
import { USER_FIELDS } from '@/graphql/queries/users';
import { gql } from '@apollo/client';
import { LISTING_FIELDS } from '@/graphql/queries/listings';
import { SURVEY_QUESTION_FIELDS, SURVEY_RESPONSE_FIELDS } from '@/graphql/fragments/surveys';

export const QUERY_STOP_MESSAGES = gql`
    ${MESSAGE_FIELDS}

    query GetStopMessages($order_ids: [uuid!] = []) {
        messages(where: { order_id: { _in: $order_ids } }) {
            ...MessageFields
        }
    }
`;

export const QUERY_PLANNING_DATA = gql`
    ${ROUTE_FIELDS}
    ${ORDER_FIELDS}
    ${ORDER_ROUTE_FIELDS}
    ${ROUTE_ORDER_FIELDS}
    ${STOP_FIELDS}
    ${ITEM_FIELDS}
    ${SURVEY_RESPONSE_FIELDS}
    ${SURVEY_QUESTION_FIELDS}
    ${ROUTE_STAGING_VERIFICATION_FIELDS}

    query QueryPlanningData(
        $from_date: timestamptz!
        $to_date: timestamptz!
        $user_id: uuid!
        $orderCondition: orders_bool_exp!
        $dropoffCondition: orders_bool_exp!
        $pickupCondition: orders_bool_exp!
    ) {
        routes(
            where: {
                _and: [
                    { shipper_id: { _eq: $user_id } }
                    { scheduled_delivery: { _gte: $from_date } }
                    { scheduled_delivery: { _lt: $to_date } }
                ]
            }
            order_by: { route_number: asc }
        ) {
            ...RouteFields
            ...RouteOrderFields
            ...RouteStagingVerificationFields
            stopsByRouteId(order_by: { ordering: asc }) {
                ...StopFields
            }
            teammateByDriverId {
                teammate_id
                username
            }
        }
        # Bucket where-clauses (pickups + dropoffs) are mirrored client-side by
        # graphql/planningBuckets.js — changing this shape requires updating
        # that module.
        pickups: orders(
            where: {
                _and: [
                    $orderCondition
                    $pickupCondition
                    { order_status: { _in: ["pending", "claimed", "routed", "routedActive"] } }
                    { order_type: { _nin: ["wh_inventory", "will_call", "wh_return"] } }
                    { wh_events: { transition: { next: { _eq: "CD_PENDING_PO" } } } }
                    {
                        _not: {
                            routes: {
                                _and: [
                                    { type: { _in: ["PICKUP", "FULL"] } }
                                    { duplicate_override: { _eq: "DISCARD" } }
                                ]
                            }
                        }
                    }
                ]
            }
        ) {
            ...OrderFields
            ...OrderRouteFields
            itemsByOrderId {
                ...ItemFields
            }
            items @client {
                ...ItemFields
            }
            returns @client {
                ...ItemFields
            }
            services {
                service_id
                service_time
                quantity
            }
            survey_responses {
                ...SurveyResponseFields
                survey_question {
                    ...SurveyQuestionFields
                }
            }
        }
        # Mirrored client-side by graphql/planningBuckets.js — changing this
        # shape requires updating that module.
        dropoffs: orders(
            where: {
                _and: [
                    $orderCondition
                    $dropoffCondition
                    { order_status: { _in: ["pending", "claimed", "routed", "routedActive"] } }
                    { order_type: { _nin: ["wh_inventory", "will_call", "wh_return"] } }
                    {
                        _not: {
                            routes: {
                                _and: [
                                    { type: { _in: ["DROPOFF", "FULL"] } }
                                    { duplicate_override: { _eq: "DISCARD" } }
                                ]
                            }
                        }
                    }
                ]
            }
        ) {
            ...OrderFields
            ...OrderRouteFields
            itemsByOrderId {
                ...ItemFields
            }
            items @client {
                ...ItemFields
            }
            returns @client {
                ...ItemFields
            }
            services {
                service_id
                service_time
                quantity
            }
            survey_responses {
                ...SurveyResponseFields
                survey_question {
                    ...SurveyQuestionFields
                }
            }
        }
    }
`;

export const LOADBOARD_QUERY = gql`
    ${ORDER_FIELDS}
    ${ITEM_FIELDS}
    ${CLIENT_FIELDS}
    ${USER_FIELDS}
    ${LISTING_FIELDS}
    query FetchLoadboardOrders($end: timestamptz, $user_id: uuid!, $test_acc: Boolean!, $currentTime: timestamptz) {
        orders(
            where: {
                _and: [
                    { order_status: { _in: ["open", "pending"] } }
                    { _not: { routes: {} } }
                    { oms: { _eq: false } }
                    { first_available_date: { _lt: $end } }
                    { order_shipper: { test_acc: { _eq: $test_acc } } }
                    { shipper_id: { _neq: $user_id } }
                    { carrier_id: { _is_null: true } }
                    { listing: { expiration_date: { _gte: $currentTime } } }
                ]
            }
        ) {
            ...OrderFields
            order_carrier {
                ...ClientFields
            }
            order_shipper {
                ...ClientFields
                user {
                    ...UserFields
                }
            }
            listing {
                ...ListingFields
            }
            itemsByOrderId {
                ...ItemFields
            }
            items @client {
                ...ItemFields
            }
            returns @client {
                ...ItemFields
            }
        }
    }
`;
export const NETWORK_ROUTE_QUERY = gql`
    ${ROUTE_FIELDS}
    ${ORDER_FIELDS}
    ${ROUTE_ORDER_FIELDS}
    ${STOP_FIELDS}
    ${ITEM_FIELDS}
    query FetchNetworkRoutes($from_date: timestamptz, $to_date: timestamptz, $user_id: uuid!, $test_acc: Boolean!) {
        routes(
            where: {
                _and: [
                    { status: { _in: ["active", "pending", "planning"] } }
                    { scheduled_delivery: { _gte: $from_date } }
                    { scheduled_delivery: { _lt: $to_date } }
                    { route_shipper: { test_acc: { _eq: $test_acc } } }
                    { route_shipper: { client_id: { _neq: $user_id } } }
                    { route_shipper: { client_id: { _is_null: false } } }
                    {
                        _or: [
                            { route_carrier: { data_share: { _eq: true } } }
                            { route_shipper: { data_share: { _eq: true } } }
                        ]
                    }
                ]
            }
        ) {
            ...RouteFields
            ...RouteOrderFields
            stopsByRouteId(order_by: { ordering: asc }) {
                ...StopFields
            }
        }
    }
`;

// The submit / retry / batch-confirmation REST endpoints (onward-functions
// routeSubmit / timeframeConfirmation) return no entity payload — this narrow
// network-only read replaces a whole-board refetch, targeting exactly the
// routes/orders a submit touched. Those endpoints also write
// stops.original_del_window_start/end server-side, so the route selection
// includes the ordered stop list; the replace merge on routes.stopsByRouteId
// makes the incoming set authoritative.
export const SUBMIT_RECONCILE = gql`
    ${ROUTE_FIELDS}
    ${STOP_FIELDS}
    query SubmitReconcile($route_ids: [uuid!]!, $order_ids: [uuid!]!) {
        routes(where: { route_id: { _in: $route_ids } }) {
            ...RouteFields
            stopsByRouteId(order_by: { ordering: asc }) {
                ...StopFields
            }
        }
        orders(where: { order_id: { _in: $order_ids } }) {
            order_id
            delivery_time_confirmed
            confirmation_source
            twilio_sid
            original_del_window_start
            original_del_window_end
            last_modified_at
        }
    }
`;

export const TIMEFRAME_CONFIRMATION_SUBSCRIPTION = gql`
    subscription TimeframeConfirmationSubscription($order_ids: [uuid!] = []) {
        orders(where: { order_id: { _in: $order_ids } }) {
            order_number
            order_id
            delivery_time_confirmed
            confirmation_source
            last_modified_at
        }
    }
`;

export const ROUTE_STATUS_SUBSCRIPTION = gql`
    subscription RouteStatusSubscription($route_id: uuid!) {
        routes_by_pk(route_id: $route_id) {
            status
            route_id
            route_number
            need_to_submit
        }
    }
`;

export const GET_ROUTE_ALIAS = gql`
    query GetRouteAlias($route_id: uuid!) {
        routes_by_pk(route_id: $route_id) {
            route_id
            route_alias
            resource {
                resource_id
                name
            }
        }
    }
`;

export const FETCH_ORDERS_BY_IDS = gql`
    ${ORDER_FIELDS}
    ${ORDER_ROUTE_FIELDS}
    query FetchOrdersByIds($order_ids: [uuid!]!) {
        orders(where: { order_id: { _in: $order_ids } }) {
            ...OrderFields
            ...OrderRouteFields
        }
    }
`;
